import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { zervantSettingsQueryKey } from '@features/invoicingSettings/constants/zervantSettingsQueryKey'
import { useUpdateZervantSettings } from '@features/invoicingSettings/hooks/useUpdateZervantSettings'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { useUpdateOrganization, useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../../enums/queryKeys'
import { useOnboardingFormContext } from '../../../../../contexts/OnboardingFormContext'
import { OnboardingForm } from '../../../../../types/onboardingForm'
import { getDefaultTradeNameLogoId } from '../utils/getDefaultTradeNameLogoId'
import { getInvoicesStepSettingsPayload } from '../utils/getInvoicesStepSettingsPayload'

export const useSubmitInvoicesStepForm = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { organization } = useUserOrganization()
  const { onboardingForm } = useOnboardingFormContext()
  const { updateAsync: updateOrganization, isProcessing: isUpdatingOrganization } = useUpdateOrganization()
  const { data: zervantSettings } = useZervantSettings()
  const { mutateAsync: updateZervantSettings, isProcessing: isUpdatingZervantSettings } = useUpdateZervantSettings()

  const { formState } = onboardingForm
  const isSubmitting = isUpdatingOrganization || isUpdatingZervantSettings

  const submitForm = useCallback(
    async (values: OnboardingForm) => {
      if (!organization) {
        return
      }
      const {
        company: { legalStatus, ...company },
        companyLogo,
      } = values
      const submitPromises: Promise<unknown>[] = []

      const currentLogoId = getDefaultTradeNameLogoId(zervantSettings?.values)
      const hasLogoChanged = !!companyLogo && companyLogo.fileId !== currentLogoId
      const hasLegalStatusChanged = legalStatus !== zervantSettings?.values.company.legalStatus

      const hasCompanyChanged = !!formState.dirtyFields.company
      const isZervantSettingsChanged = zervantSettings && (hasLogoChanged || hasLegalStatusChanged)

      if (isZervantSettingsChanged) {
        submitPromises.push(
          updateZervantSettings({
            values: getInvoicesStepSettingsPayload({
              legalStatus,
              logo: companyLogo,
              zervantSetting: zervantSettings.values,
            }),
          }).then(() => {
            queryClient.invalidateQueries(QueryKeys.ZervantInvoiceLogo)
            queryClient.invalidateQueries(zervantSettingsQueryKey)
          }),
        )
      }

      if (hasCompanyChanged) {
        submitPromises.push(updateOrganization({ organizationId: organization.id, payload: company }))
      }

      try {
        await Promise.all(submitPromises)
      } catch (error) {
        notify({
          id: NotificationKeys.OnboardingStepSubmitError,
          message: t('error_saving'),
          variant: 'error',
        })
        throw error
      }
    },
    [
      formState.dirtyFields.company,
      organization,
      queryClient,
      t,
      updateOrganization,
      updateZervantSettings,
      zervantSettings,
    ],
  )

  return {
    isSubmitting,
    submitForm,
  }
}
